import { formatPeso } from '@tahi/core';

const peso = (minor: number) => formatPeso(minor);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export const copy = {
  moduleName: 'Shopping list',
  navLabel: 'Shopping',
  loading: 'Loading…',
  undo: 'Undo',
  save: 'Save',
  remove: 'Remove',
  restore: 'Restore',
  delete: 'Delete',
  confirmDelete: 'Tap again to delete',
  pages: 'Shopping pages',

  // The list
  addPlaceholder: 'Add an item',
  addLabel: 'Item name',
  add: 'Add',
  moreDetails: 'Quantity, price, store',
  fewerDetails: 'Fewer details',
  quantity: 'Quantity',
  unit: 'Unit',
  unitPlaceholder: 'kg, pc, pack',
  estimate: 'Estimated price',
  estimateHint: (last: number) => `Leave it blank to use the last price, ${peso(last)}.`,
  store: 'Store',
  suggestions: 'Past items',
  suggestionLast: (amount: number) => `last ${peso(amount)}`,
  empty: 'Nothing on your list. Add what you need to buy.',
  summary: (count: number, estimate: number) =>
    estimate > 0 ? `${plural(count, 'item')} · ${peso(estimate)} estimated` : plural(count, 'item'),
  tick: (name: string) => `Bought ${name}`,
  lastPrice: (amount: number) => `Last ${peso(amount)}`,
  lastPriceText: (text: string) => `Last ${text}`,
  cheapestAt: (store: string) => `cheapest at ${store}`,
  buyButton: (count: number, estimate: number) =>
    estimate > 0
      ? `Buy ${plural(count, 'item')} · ${peso(estimate)}`
      : `Buy ${plural(count, 'item')}`,
  added: (name: string) => `Added ${name}.`,
  removed: (name: string) => `Removed ${name}.`,
  editTitle: 'Edit item',
  notFound: 'This item is gone.',

  // Buying
  howMuch: 'How much?',
  totalLabel: 'Total paid',
  estimatesNote: (amount: number) => `Your estimates add up to ${peso(amount)}.`,
  priceEach: 'Price each item',
  priceEachHint: 'Item prices add up to the total. Leave one blank if you don’t know it.',
  priceFor: (name: string) => `Price for ${name}`,
  logsExpense: 'Logs one expense in Spending under Groceries.',
  noSpending: 'Spending is off, so this records the trip and prices only.',
  bought: (count: number, amount: number) => `Bought ${plural(count, 'item')} for ${peso(amount)}.`,
  boughtCta: (amount: number | null) => (amount ? `Bought · ${peso(amount)}` : 'Bought'),

  // Trips
  pastTrips: 'Past trips',
  noTrips: 'No trips yet. Tick items on your list when you buy them.',
  tripTitle: 'Trip',
  items: (n: number) => plural(n, 'item'),
  tripCard: (date: string, store: string | null, amount: string) =>
    `${date}${store ? `, ${store}` : ''}, ${amount}`,
  monthTotal: (amount: number) => `${peso(amount)} total`,
  viewExpense: 'Open the expense in Spending',
  noExpense: 'No expense was logged (Spending was off).',
  noPrice: 'No price',
  deletedTrip: 'This trip was deleted.',
  tripDeleted: 'Trip deleted.',
  tripRestored: 'Trip restored.',
  tripNotFound: 'This trip is gone.',
  history: 'History',
  historyAction: (action: string) =>
    ({
      create: 'Bought',
      update: 'Logged in Spending',
      delete: 'Deleted',
      restore: 'Restored',
    })[action] ?? action,

  // Prices
  prices: 'Prices',
  noPrices: 'Prices show here once you buy an item with its own price.',
  priceHistory: 'Price history',
  last: 'Last',
  lowest: 'Lowest',
  average: 'Average',
  change: 'Since last time',
  noChange: 'Same as last time',
  firstPrice: 'First price',
  up: (amount: string) => `▲ ${amount}`,
  down: (amount: string) => `▼ ${amount}`,
  basisUnit: (unit: string) => `Compared per ${unit}, so pack sizes compare fairly.`,
  basisPaid: 'Compared by price paid (sizes differ or weren’t entered).',
  timesBought: (n: number) => `Bought ${n === 1 ? 'once' : `${n} times`}`,
  chartLabel: (name: string) => `${name} price over time`,
  priceNotFound: 'No prices for this item yet.',

  // Tiles
  listTile: 'Shopping list',
  lastTripTile: 'Last trip',
  openList: 'Open the shopping list',
  addItem: 'Add to the shopping list',
  nothingToBuy: 'Nothing to buy',
  moreItems: (n: number) => `+${n} more`,

  // v0.7.5 menu
  tripsCount: (n: number) => plural(n, 'trip'),
  itemsCount: (n: number) => plural(n, 'item'),
};

/** v0.7.5 back targets for the sub-screens' ‹ (the logical parent). */
export const LIST_BACK = { to: '/m/shopping', label: copy.moduleName };
export const TRIPS_BACK = { to: '/m/shopping/trips', label: copy.pastTrips };
export const PRICES_BACK = { to: '/m/shopping/prices', label: copy.prices };

export function errorText(error: string): string {
  const map: Record<string, string> = {
    name_required: 'Give the item a name.',
    name_too_long: 'That name is too long.',
    store_too_long: 'That store name is too long.',
    unit_too_long: 'That unit is too long.',
    bad_quantity: 'Enter a quantity above zero.',
    bad_estimate: 'Enter a price above zero.',
    amount_required: 'Enter how much you paid.',
    amount_too_large: 'That amount is too large.',
    bad_price: 'Enter item prices above zero.',
    prices_over_total: 'Item prices add up to more than the total.',
    prices_mismatch: 'Item prices must add up to the total.',
    item_not_open: 'One of these items was already bought or removed.',
    no_items: 'Tick at least one item.',
  };
  return map[error] ?? 'Something went wrong. Try again.';
}
